import { syntaxTree, syntaxTreeAvailable } from '@codemirror/language';
import { type EditorState } from '@uiw/react-codemirror';

import { isDefined } from 'common';

import { appendArrayIndex, appendObjectKey } from '../../../../../Code/referencePath';

const JSON_VALUE_NODE_NAMES: ReadonlySet<string> = new Set([
  'Array',
  'False',
  'Null',
  'Number',
  'Object',
  'String',
  'True',
]);

function parsePropertyName(quotedName: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(quotedName);
    return typeof parsed === 'string' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

interface ResolveCodeMirrorReferencePathProps {
  state: EditorState;
  position: number;
  rootPath: string;
}

export function resolveCodeMirrorReferencePath(
  props: ResolveCodeMirrorReferencePathProps
): string | undefined {
  const { state, position, rootPath } = props;

  if (position < 0 || position > state.doc.length || !syntaxTreeAvailable(state, position)) {
    return undefined;
  }

  const tree = syntaxTree(state);
  const innermost = tree.resolveInner(position, 1);
  const ancestors = [innermost];
  for (let parent = innermost.parent; isDefined(parent); parent = parent.parent) {
    ancestors.push(parent);
  }
  ancestors.reverse();

  let path = rootPath;
  for (let depth = 0; depth < ancestors.length; depth++) {
    const node = ancestors[depth];

    if (node.name === 'Property') {
      const propertyName = node.getChild('PropertyName');
      if (!isDefined(propertyName)) {
        return undefined;
      }
      const key = parsePropertyName(state.sliceDoc(propertyName.from, propertyName.to));
      if (!isDefined(key)) {
        return undefined;
      }
      path = appendObjectKey({ path, key });
      continue;
    }

    if (node.name !== 'Array') {
      continue;
    }

    const element = ancestors[depth + 1];
    if (!isDefined(element) || !JSON_VALUE_NODE_NAMES.has(element.name)) {
      continue;
    }

    let index = 0;
    let isElementFound = false;
    for (let sibling = node.firstChild; isDefined(sibling); sibling = sibling.nextSibling) {
      if (sibling.from === element.from && sibling.to === element.to) {
        isElementFound = true;
        break;
      }
      if (JSON_VALUE_NODE_NAMES.has(sibling.name)) {
        index++;
      }
    }
    if (!isElementFound) {
      return undefined;
    }
    path = appendArrayIndex({ path, index });
  }

  return path;
}
